import React, { useState } from 'react';

const RemoveClient = ({ clientConfig }) => {
  const [clientId, setClientId] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleLookup = async () => {
    if (!clientId.trim()) { setError('Enter a client ID'); return; }
    setError(null); setResult(null); setClientInfo(null); setConfirmText('');
    try {
      const res = await fetch(`/v1/clients/${clientId}/status`);
      if (res.ok) {
        const data = await res.json();
        setClientInfo(data);
      } else {
        setError('Client not found. Check the client ID and try again.');
      }
    } catch {
      setError('Could not reach backend. Verify the server is running.');
    }
  };

  const handleRemove = async () => {
    if (confirmText !== clientId) {
      setError('Confirmation text does not match the client ID.');
      return;
    }
    setError(null); setResult(null);
    try {
      const res = await fetch(`/v1/clients/${clientId}`, { method: 'DELETE' });
      if (res.ok) {
        setResult(`Client ${clientId} has been removed and unsubscribed.`);
        setClientInfo(null); setConfirmText('');
      } else {
        const data = await res.json();
        setError(data.error || 'Removal failed.');
      }
    } catch {
      setError('Could not reach backend.');
    }
  };

  const fieldStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box',
  };

  const btnStyle = (bg) => ({
    padding: '10px 24px', background: bg, color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  });

  return (
    <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '4px' }}>🗑️ Remove / Unsubscribe Client</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Permanently remove a client and all associated data (S3 files, DB credentials, tenant records)
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <input
          type="text" placeholder="Enter client ID (e.g. client_prab_001)"
          value={clientId} onChange={e => setClientId(e.target.value)}
          style={{ ...fieldStyle, marginBottom: 0, flex: 1 }}
          aria-label="Client ID to remove"
        />
        <button onClick={handleLookup} style={btnStyle('#2196F3')}>Look Up</button>
      </div>

      {error && <p style={{ color: '#f44336', marginBottom: '12px' }}>❌ {error}</p>}
      {result && <p style={{ color: '#4CAF50', marginBottom: '12px' }}>✅ {result}</p>}

      {clientInfo && (
        <div style={{ background: '#fff3f3', padding: '20px', borderRadius: '12px', border: '1px solid #ffcdd2' }}>
          <h3 style={{ color: '#c62828', marginTop: 0 }}>⚠️ Confirm Removal</h3>

          <table style={{ width: '100%', fontSize: '13px', marginBottom: '16px' }}>
            <tbody>
              <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Company</td><td>{clientInfo.company_name}</td></tr>
              <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Client ID</td><td style={{ fontFamily: 'monospace' }}>{clientId}</td></tr>
              <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Plan</td><td>{clientInfo.plan}</td></tr>
              <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Tenant Type</td><td>{clientInfo.tenant_type}</td></tr>
              <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Status</td><td>{clientInfo.status}</td></tr>
            </tbody>
          </table>

          <p style={{ fontSize: '13px', color: '#c62828', fontWeight: 600 }}>
            This will permanently delete:
          </p>
          <ul style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
            <li>S3 files (Guardrails, LLM, Comm-AI PDFs)</li>
            <li>Secrets Manager credentials</li>
            <li>DynamoDB tenant record</li>
            <li>PostgreSQL tenant, security view, and permissions table</li>
            <li>All audit trail entries</li>
          </ul>

          <label style={{ fontWeight: 600, fontSize: '13px', display: 'block', marginBottom: '4px' }}>
            Type the client ID to confirm: <code>{clientId}</code>
          </label>
          <input
            style={{ ...fieldStyle, borderColor: '#ffcdd2' }}
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder={clientId}
            aria-label="Confirm client ID"
          />

          <button
            onClick={handleRemove}
            disabled={confirmText !== clientId}
            style={{
              ...btnStyle(confirmText === clientId ? '#c62828' : '#ccc'),
              cursor: confirmText === clientId ? 'pointer' : 'not-allowed',
            }}
          >
            Permanently Remove Client
          </button>
        </div>
      )}
    </div>
  );
};

export default RemoveClient;
